import React, { createRef, PureComponent, ReactNode, RefObject } from 'react'
import { Cluster, Marker, MarkerClusterer } from '@googlemaps/markerclusterer'
import { flatten, getRegion, loadGoogleMaps } from '../../helpers'
import { ICoordinate, ILocation, ISearchedType, IServiceInfo, IStop } from '../../models'
import theme from '../../theme'
import StyledDiv from './styles'
import { LiteralsService } from '../../services'
import { StopMarker } from '../../components'
import { images } from '../../images'
import { searchStopCoordinatesAction } from '../../store/actions'
import { connect, DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'

interface IProps extends DispatchProp<AnyAction> {
  path?: any
  serviceInfos: IServiceInfo[]
  inStop?: IStop | null
  outStop?: IStop | null
  outBusStops?: IStop[]
  busLocation?: ICoordinate | null
  correction?: number
  zoomLevel?: number
  isClusterMap?: boolean
  controlsEnabled?: boolean
  onMarkerClick?: (stop: IStop) => void
  areaChanged?: boolean
  onInfoWindowShow?: (opened: boolean) => void
  centerChanged?: ICoordinate | null
  showRecenterButton?: boolean
  userLocation?: ICoordinate
  onSetUserLocation?: () => void
  restrictedOrigins?: number[]
}

interface IState {
  isMapCentered: boolean
  forwardDirection: boolean
}

class MapContainer extends PureComponent<IProps, IState> {
  public map!: google.maps.Map
  private mapRef: RefObject<HTMLDivElement>
  private infoWindow!: google.maps.InfoWindow
  private markers!: google.maps.marker.AdvancedMarkerElement[]
  private clusterer!: MarkerClusterer
  private polygons: google.maps.Polygon[] = []
  private userLocationMarker!: google.maps.marker.AdvancedMarkerElement
  private busLocationMarker!: google.maps.marker.AdvancedMarkerElement

  private get noOutline(): boolean {
    const { serviceInfos } = this.props

    return serviceInfos.every(s => !s.outlines)
  }

  private get region(): any {
    const { serviceInfos, isClusterMap, inStop, outStop } = this.props
    const serviceInfosNotSelected = this.props.serviceInfos.every((serviceInfo: IServiceInfo) => !serviceInfo.selected)

    if (serviceInfos && serviceInfos.length && isClusterMap) {
      return getRegion({
        features: serviceInfos
            .filter(s => serviceInfosNotSelected ? s : s.selected)
            .map(s => s.stops.map(st => st.location))
            .reduce(flatten, [])
      })
    } else if (!isClusterMap && (inStop || outStop)) {
      let completeFeatures: any = []
      if (inStop) completeFeatures = [completeFeatures, inStop.location].concat()
      if (outStop) completeFeatures = [completeFeatures, outStop.location].concat()
      return getRegion({
        features: completeFeatures
      })
    }

    return null
  }

  constructor(props: IProps) {
    super(props)
    this.mapRef = createRef<HTMLDivElement>()

    this.state = {
      isMapCentered: true,
      forwardDirection: true
    }
  }

  public componentDidMount(): void {
    this.drawMap()
    this.drawUserLocationMarker()
  }

  public componentDidUpdate(prevProps: IProps): void {
    const { areaChanged, centerChanged, serviceInfos, userLocation, isClusterMap, inStop, outStop, busLocation, outBusStops, controlsEnabled, zoomLevel } = this.props
    const { isMapCentered } = this.state

    if (!this.map) {
      this.drawMap()
      return
    }

    if (prevProps.controlsEnabled !== controlsEnabled) {
      this.map.setOptions({
        zoomControl: !!controlsEnabled,
        fullscreenControl: !!controlsEnabled,
        draggable: !!controlsEnabled
      })
    }

    if (prevProps.isClusterMap !== isClusterMap) {
      this.tearDownMarkers()
      this.clearOutlines()
      this.drawServiceOutlines()
      this.drawMarkers()
      this.setMapBoundariesAndZoom()
      if (zoomLevel) this.map.setZoom(zoomLevel)
      return
    }

    const markersNeedUpdate = isClusterMap && (
      prevProps.inStop !== inStop ||
      prevProps.outStop !== outStop ||
      prevProps.serviceInfos !== serviceInfos ||
      prevProps.outBusStops !== outBusStops
    )

    if (markersNeedUpdate) {
      this.updateMarkersIcon()
      this.setMapBoundariesAndZoom()
    }

    if (!isClusterMap && (prevProps.inStop !== inStop || prevProps.outStop !== outStop)) {
      this.tearDownMarkers()
      this.drawMarkers()
      this.setMapBoundariesAndZoom()
    }

    if (!isMapCentered && ((prevProps.inStop && !inStop) || (prevProps.outStop && !outStop))) this.handleRecenterClick()

    if (centerChanged) this.updateMapLocation(centerChanged.latitude, centerChanged.longitude)
    if (userLocation) this.drawUserLocationMarker()
    if (areaChanged) this.updateMapLocation(this.region.latitude, this.region.longitude)
    if (prevProps.serviceInfos !== serviceInfos) {
      this.clearOutlines()
      this.drawServiceOutlines()
      this.clusterer && this.clusterer.clearMarkers()
      this.drawMarkers()
      this.setMapBoundariesAndZoom()
    }
    if (busLocation) this.drawBusLocation()
    else if (this.busLocationMarker) this.busLocationMarker.map = null
  }

  private tearDownMarkers(): void {
    if (this.clusterer) {
      this.clusterer.clearMarkers()
    }
    if (this.markers) {
      this.markers.forEach(m => { m.map = null })
      this.markers = []
    }
  }

  public render(): ReactNode {
    const { showRecenterButton } = this.props
    const { isMapCentered } = this.state

    return (
      <>
        <StyledDiv className='markers-options'>
          <StopMarker id='normalMarker' color={ theme.colors.black } />
          <StopMarker id='originMarker' color={ theme.colors.blueLight } text={ LiteralsService.get('origin', true) } textColor={ theme.colors.white } />
          <StopMarker id='destinationMarker' color={ theme.colors.greenRadio } text={ LiteralsService.get('destination', true) } />
          <StopMarker id='disabledMarker' color={ theme.colors.greyMedium } />
          <StopMarker id='originDot' color={ theme.colors.blueLight } />
          <StopMarker id='destinationDot' color={ theme.colors.greenRadio } />
        </StyledDiv>
        <StyledDiv id='persistent-map-mount'>
          <StyledDiv id="map" className="map" ref={ this.mapRef } />
          {
            showRecenterButton &&
            <div className='recenter-container' onClick={ () => this.handleRecenterClick() }>
              <img src={ isMapCentered ? images['myLocation'] : images[`area`] } alt='recenter' />
            </div>
          }
        </StyledDiv>
      </>
    )
  }

  private async drawMap(): Promise<void> {
    await loadGoogleMaps()
    const { controlsEnabled, zoomLevel} = this.props
    const latitude = this.region?.latitude
    const longitude = this.region?.longitude

    if (this.mapRef && this.mapRef.current && latitude && longitude) {
      this.map = new google.maps.Map(this.mapRef.current, {
        center: { lat: latitude, lng: longitude },
        controlSize: 26,
        zoom: zoomLevel ?? 14,
        zoomControl: !!controlsEnabled,
        zoomControlOptions: {
          position: 3
        },
        fullscreenControl: !!controlsEnabled,
        draggable: !!controlsEnabled,
        panControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        clickableIcons: false,
        mapId: process.env.REACT_APP_GOOGLE_MAP_ID
      })
      ;(window as any).__persistentMap = this.map
      ;(window as any).__refreshPersistentMap = () => {
        if (!this.map) return
        google.maps.event.trigger(this.map, 'resize')
        if (this.clusterer && this.markers && this.markers.length) {
          this.clusterer.clearMarkers()
          this.clusterer.addMarkers(this.markers)
        } else if (this.markers && this.markers.length) {
          this.markers.forEach(m => { m.map = null })
          this.markers.forEach(m => { m.map = this.map })
        }
        if (this.markers && this.markers.length) this.setMapBoundariesAndZoom()
      }

      this.initInfoWindow()
      this.drawMarkers()
      this.drawRoute()
      this.drawServiceOutlines()
      this.setMapBoundariesAndZoom()

      this.map.addListener('dragstart', () => {
        this.setState({
          isMapCentered: false
        })
      })

      this.map.addListener('zoom_changed', () => {
        this.setState({
          isMapCentered: false
        })
      })

      this.map.addListener('click', () => {
        if (this.infoWindow.isOpen) this.infoWindow.close()
      })
    }
  }

  private initInfoWindow() {
    const { onInfoWindowShow } = this.props

    const handleInfoWindowChange = (opened: boolean) => {
      if (onInfoWindowShow && typeof onInfoWindowShow === 'function') onInfoWindowShow(opened)
    }

    this.infoWindow = new google.maps.InfoWindow()
    google.maps.event.addListener(this.infoWindow, 'close', () => handleInfoWindowChange(false))
    google.maps.event.addListener(this.infoWindow, 'visible', () => handleInfoWindowChange(true))
    google.maps.event.addListener(this.map, 'drag', () => {
      if (this.infoWindow.isOpen) this.infoWindow.close()
    })
  }

  private drawMarkers(): any {
    const { isClusterMap, serviceInfos, inStop, outStop, correction } = this.props
    const latCorrection = correction ?? 0

    if (isClusterMap && serviceInfos) this.fillClusterMarkers()
    else if (!isClusterMap) {
      this.markers = []
      if (inStop) {
        const beginMarker = document.getElementById('originDot')?.cloneNode(true)
        const originMarker = new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          gmpDraggable: false,
          position: {
            lat: inStop.location.geometry.coordinates[1] - latCorrection,
            lng: inStop.location.geometry.coordinates[0]
          },
          content: beginMarker
        })
        this.markers.push(originMarker)
      }
      if (outStop) {
        const endMarker = document.getElementById('destinationDot')?.cloneNode(true)
        const destinationMarker = new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          gmpDraggable: false,
          position: {
            lat: outStop.location.geometry.coordinates[1] - latCorrection,
            lng: outStop.location.geometry.coordinates[0]
          },
          content: endMarker
        })
        this.markers.push(destinationMarker)
      }
    }
  }

  private async drawUserLocationMarker(): Promise<void> {
    await loadGoogleMaps()
    const { userLocation, onSetUserLocation } = this.props
    this.userLocationMarker && this.userLocationMarker.remove();
    if (userLocation) {
      const userInfoImg = document.createElement('img')
      userInfoImg.src = images.userInfo
      userInfoImg.width = 24
      userInfoImg.height = 24
  
      const pin = new google.maps.marker.PinElement({
        scale: 1.5,
        background: '#FFF',
        glyph: userInfoImg,
      })
  
      this.userLocationMarker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        gmpDraggable: false,
        position: {
          lat: userLocation.latitude,
          lng: userLocation.longitude
        },
        content: pin.element
      })
      
      const userLocationLatLng = new google.maps.LatLng(userLocation.latitude, userLocation.longitude);
      this.map.setCenter(userLocationLatLng);
      this.setState({
        isMapCentered: false
      })
      onSetUserLocation && onSetUserLocation()
    }
  }
  
  private async drawBusLocation() {
    const { busLocation } = this.props
    if (this.busLocationMarker) this.busLocationMarker.map = null
    if (busLocation) {
      const iconContainer = document.createElement('div')
      iconContainer.style.width = '30px'
      iconContainer.style.height = '30px'
      iconContainer.style.padding = '4px'
      iconContainer.style.borderRadius = '50%'
      iconContainer.style.border = `2px solid ${busLocation.color}`
      iconContainer.style.backgroundColor = 'white'
      iconContainer.style.display = 'flex'
      iconContainer.style.justifyContent = 'center'
      iconContainer.style.alignItems = 'center'

      const busIcon = document.createElement('div')
      busIcon.style.width = '24px'
      busIcon.style.height = '24px'
      busIcon.style.backgroundColor = busLocation.color || 'black'
      busIcon.style.mask = `url(${images.bus})`

      iconContainer.appendChild(busIcon)
  
      this.busLocationMarker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        gmpDraggable: false,
        position: {
          lat: busLocation.latitude,
          lng: busLocation.longitude
        },
        content: iconContainer
      })
    }
  }

  private getStopType(stop: IStop | undefined): string {
    if (stop) {
      const { inStop, outStop } = this.props
      const isOriginStop = stop.id === inStop?.id
      const isDestinationStop = stop.id === outStop?.id
      if ((!isOriginStop && !isDestinationStop) && this.isDisabled(stop)) return 'disabled'
      return isOriginStop
      ? 'origin'
      : isDestinationStop
        ? 'destination'
        : 'normal'
    }
    return 'normal'
  }

  private fillClusterMarkers(): void {
    const { serviceInfos, correction, inStop, outStop, onMarkerClick } = this.props
    const { forwardDirection } = this.state
    const latCorrection = correction ?? 0

    this.markers = [];

    const selectesServiceInfos = serviceInfos.filter((serviceInfo) => serviceInfo.selected)
    selectesServiceInfos.map(serviceInfo => {
      serviceInfo.stops.filter((s: any) => forwardDirection ? (s.direction == 0 || s.direction == null) : (s.direction == 1 || s.direction == null)).map(stop => {
        const lat = stop.location.geometry.coordinates[1] - latCorrection
        const lng = stop.location.geometry.coordinates[0]
        const isStopDisabled = this.isDisabled(stop)
        const stopType = this.getStopType(stop)
        let stopMarkerId = `${stopType}Marker`

        const markerIcon = document.getElementById(stopMarkerId)
        
        const marker = new google.maps.marker.AdvancedMarkerElement({
          gmpDraggable: false,
          position: { lat, lng },
          content: markerIcon?.cloneNode(true) as HTMLElement
          
        })
        marker.setAttribute('id', `${serviceInfo.id}_${stop.id}_${stopType}`)
        marker.addListener('click', () => {
          const stopData = { ...stop, serviceInfo }
          if (inStop && inStop.id === stop.id) {
            onMarkerClick && onMarkerClick(stopData)
          } else if (outStop && outStop.id === stop.id) {
            onMarkerClick && onMarkerClick(stopData)
          } else {
            if (!isStopDisabled) this.drawMarkerInfo(stop, marker, serviceInfo)
          }
        })
        if (!this.markers.find(marker => Number(marker.getAttribute('id')?.split('_')[1]) === stop.id)) this.markers.push(marker)
      })
    })

    if (this.clusterer) {
      this.clusterer.addMarkers(this.markers)
    } else {
      this.clusterer = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        renderer: {
          render: (cluster: Cluster) => this.renderCluster(cluster, serviceInfos)
        }
      })
    }
  }

  private clusterHasEveryStopType(clusterPoints: any, type: string) {
    return clusterPoints.every((point: any) => {
      const stopType = point.id.split('_')[2]
      return stopType === type
    })
  }

  private clusterHasSomeStopType(clusterPoints: any, type: string) {
    return clusterPoints.some((point: any) => {
      const stopType = point.id.split('_')[2]
      return stopType === type
    })
  }

  private renderCluster(cluster: Cluster, serviceInfos: IServiceInfo[]): Marker {
    const clusteredPoints = cluster.markers || []
    let clusterStopType = 'normal'

    if (this.clusterHasEveryStopType(clusteredPoints, 'disabled')) clusterStopType = 'disabled'
    else if (this.clusterHasSomeStopType(clusteredPoints, 'origin')) clusterStopType = 'origin'
    else if (this.clusterHasSomeStopType(clusteredPoints, 'destination')) clusterStopType = 'destination'

    const clusterIcon = document.getElementById(`${clusterStopType}Marker`)?.cloneNode(true) as HTMLElement
    if (clusterIcon) clusterIcon.style.scale = '1.2'
    return new google.maps.marker.AdvancedMarkerElement({
      gmpDraggable: false,
      position: cluster.position,
      content: clusterIcon
    })
  }

  private drawMarkerInfo(stop: IStop, marker: google.maps.marker.AdvancedMarkerElement, serviceInfo: IServiceInfo) {
    const { onMarkerClick } = this.props
    const { isMapCentered } = this.state
    
    const selectedMarkerId = this.infoWindow.get('markerId')
    if (selectedMarkerId && selectedMarkerId === marker.id && this.infoWindow.isOpen) {
      this.infoWindow.close()
    } else {
      const stopType = this.getStopType(stop)
      const stopMarkerContent = document.getElementById(stopType === 'origin' || stopType === 'destination' ? `${stopType}Dot` : `${stopType}Marker`)?.cloneNode(true) as HTMLElement
  
      const contentDiv = document.createElement('div')
      contentDiv.classList.add('marker-infowindow')
      contentDiv.innerHTML = `
        <span>${ stop.name }</span>
        <div>
          ${ stopMarkerContent.getHTML() }
        </div>
      `
      if (onMarkerClick && typeof onMarkerClick === 'function') {
        contentDiv.onclick = () =>  {
          onMarkerClick({ ...stop, serviceInfo })
          if (!isMapCentered) this.handleRecenterClick()
          // this.updateMarkersIcon()
          this.infoWindow.setOptions({  })
          this.infoWindow.close()
        }
      }
  
      this.infoWindow.close()
      this.infoWindow.setHeaderDisabled(true)
      this.infoWindow.setContent(contentDiv)
      this.infoWindow.set('markerId', marker.id)
      this.infoWindow.open(this.map, marker)
    }
  }

  private updateMarkersIcon = (): void => {
    this.clusterer && this.clusterer.clearMarkers()
    this.markers = []
    this.fillClusterMarkers()
  }

  private updateMapLocation(lat: number, lng: number): void {
    const { zoomLevel } = this.props
    const center = new google.maps.LatLng(lat, lng)
    this.map.panTo(center)
    this.map.setZoom(zoomLevel ?? 14)
    this.setMapBoundariesAndZoom()
  }

  private drawRoute(): void {
    const { path } = this.props

    if (path) {
      const coordinates: google.maps.LatLngLiteral[] = path.features
        .filter((c: any) => c !== null)
        .flatMap((feature: any) => {
          return feature.geometry.coordinates.map((coord: any) => {
            return { lat: coord[1], lng: coord[0] }
          })
        })
      const busPath = new google.maps.Polyline({
        path: coordinates,
        geodesic: false,
        strokeColor: '#000000',
        strokeOpacity: 1.0,
        strokeWeight: 2.5
      })
      busPath.setMap(this.map)
    }
  }

  private setMapBoundariesAndZoom() {
    let markersAvailables: any[] = [];
    this.markers.forEach((marker) => {
      if (!marker.id.includes('disabled')) markersAvailables.push(marker)
    })
    const bounds = new google.maps.LatLngBounds()
    const coordinates: google.maps.LatLngLiteral[] = markersAvailables.map(m => m.position as google.maps.LatLngLiteral )
    coordinates.map(c => bounds.extend(c))
    this.map.fitBounds(bounds, {top: 55, right: 30, bottom: 55, left: 30})
  }

  private clearOutlines(): void {
    this.polygons.map((outline) => outline.setMap(null))
    this.polygons = []
  }

  private drawServiceOutlines(): void {
    const { isClusterMap, serviceInfos } = this.props
    const serviceInfoNotSelected = serviceInfos.every((serviceInfo) => !serviceInfo.selected)
    if (isClusterMap && serviceInfos) {
      serviceInfos.filter((serviceInfo: IServiceInfo) => serviceInfoNotSelected ? serviceInfo : !!serviceInfo.outlines && serviceInfo.selected)
      .forEach(serviceInfo => {
        serviceInfo.outlines.map((outline) => {
          if (outline && outline.geometry) {
            const outlineCoords: google.maps.LatLngLiteral[] = outline.geometry.coordinates
              .map((coord: any) => {
                return coord.map((c: any) => ({ lat: c[1], lng: c[0] }))
              })
            const polygon = new google.maps.Polygon({
              paths: outlineCoords,
              strokeColor: serviceInfo.color,
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: `${serviceInfo.color}`,
              fillOpacity: 0.35
            })
            polygon.setMap(this.map)
            this.polygons.push(polygon)
          }
        })
      })
    }
  }

  private isDisabled(stop: IStop): boolean {
    const { outBusStops, inStop, outStop, restrictedOrigins } = this.props
    if ((inStop || outStop) && !!outBusStops && outBusStops.length === 0) return true

    const isSelectable = !!outBusStops && outBusStops.some(dbs => dbs.id === stop.id)
    const isOrigin =
      inStop == null
        ? false
        : inStop && inStop.id === stop.id

    const checkIfIsRestricted = restrictedOrigins ? restrictedOrigins.includes(stop.id) : false;
    if (isOrigin) return false
    return inStop != null ? !!outBusStops && !!outBusStops.length && !isSelectable : checkIfIsRestricted
  }

  private handleRecenterClick() {
    const { dispatch } = this.props
    const { isMapCentered } = this.state
    if (isMapCentered) {
      dispatch(searchStopCoordinatesAction({ type: ISearchedType.location } as any))
    } else {
      this.updateMapLocation(this.region.latitude, this.region.longitude)
      this.setState({
        isMapCentered: true
      })
    }
  }
}

export default connect()(MapContainer)
